import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import {
  Sparkles, Brain, CheckCircle2, XCircle, BarChart3, AlertTriangle,
  SlidersHorizontal, ChevronDown, Check, X, RefreshCw,
  Layers, Zap, Loader2 } from
"lucide-react";
import { Requirement, LenderTag, LenderStatus } from "../../types/deals";
import { ManagedLender, SimulationOverride, InvestmentPreference } from "../../types/lender";
import { type EligibilityResult, type AllocationCombination, type FieldCheck } from "../../types/lenderMatching";
import { fetchLenderMatch, toMatchRequirement } from "../../services/lenderMatchingService";
import { getStatusConfig } from "../../utils/statusConfig";
import { Avatar, AvatarImage } from "../../components/ui/avatar";

/** Shared tab styles — always use product brand colors for active state */
const MATCH_TAB_TRIGGER =
  "group inline-flex h-8 w-full items-center justify-center gap-1 rounded-md border border-transparent px-2 text-xs font-medium transition-all duration-200 " +
  "text-muted-foreground hover:bg-muted/60 hover:text-foreground " +
  "data-[state=active]:!border-[hsl(var(--brand)/0.35)] data-[state=active]:!bg-[hsl(var(--brand-subtle))] " +
  "data-[state=active]:!text-[hsl(var(--brand))] data-[state=active]:!shadow-none data-[state=active]:font-semibold " +
  "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0";

const MATCH_TAB_BADGE =
  "ml-0.5 h-4 border-0 px-1 text-[10px] bg-muted/80 text-muted-foreground " +
  "group-data-[state=active]:bg-[hsl(var(--brand)/0.12)] group-data-[state=active]:text-[hsl(var(--brand))]";

interface AIMatchPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requirement: Requirement | null;
}

function formatAmount(n: number) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
}

function formatAmountCompact(n: number): string {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1).replace(/\.0$/, '')} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.0$/, '')} L`;
  return `₹${formatAmount(n)}`;
}

function getLenderAvatarUrl(lender: ManagedLender | undefined): string {
  const src =
    (lender as { avatar?: string; logo?: string } | undefined)?.avatar ??
    lender?.logo ??
    "";
  if (!src) return "";
  return src.startsWith("http") ? src : `${import.meta.env.VITE_API_BASE_URL}/${src}`;
}

function LenderLogo({ lender }: { lender: ManagedLender | undefined }) {
  const avatarSrc = getLenderAvatarUrl(lender);
  return (
    <Avatar className="rounded-lg shrink-0" style={{ height: "50px", width: "80px" }}>
      <AvatarImage
        src={avatarSrc}
        alt={lender?.name ?? "Lender"}
        className="object-contain"
      />
    </Avatar>
  );
}

function getLenderTag(requirement: Requirement, lender: ManagedLender | undefined): LenderTag | undefined {

  if (!lender) return undefined;
  const name = (lender.name || "").toLowerCase().trim();
  return requirement.lenders?.find(
    (lt) =>
      lt.institutionId === lender.id ||
      String(lt.institutionId) === String(lender.id) ||
      (lt.institutionName || "").toLowerCase().trim() === name
  );
}

function getLenderTagStatus(requirement: Requirement, lender: ManagedLender | undefined): string | null {
  const tag = getLenderTag(requirement, lender);

  if (!tag || tag.statusHistory.length === 0) return null;
  return tag.statusHistory[tag.statusHistory.length - 1].status ?? null;
}

function isLenderRejected(requirement: Requirement, lender: ManagedLender | undefined): boolean {
  return getLenderTagStatus(requirement, lender) === "Rejected";
}

function filterRejectedFromCombo(
  requirement: Requirement,
  combo: AllocationCombination,
  quantum: number
): AllocationCombination {
  const entries = (combo.entries ?? []).filter(
    (e) => e?.lender && !isLenderRejected(requirement, e.lender)
  );
  const totalAllocated = entries.reduce((sum, e) => sum + Number(e?.allocation ?? 0), 0);
  const shortfall = Math.max(0, quantum - totalAllocated);
  return {
    ...combo,
    entries,
    totalAllocated,
    shortfall,
    fulfilled: shortfall <= 0,
    lendersUsed: entries.length,
  };
}

// Lower rank = better (will appear first)
const TAG_STATUS_RANK: Record<string, number> = {
  "Disbursed": 1,
  "Sanctioned": 2,
  "Term Sheet Shared": 3,
  "Under Review": 3,
  "Credit Discussion Completed": 3,
  "Deal Shared": 3,
  "Unresponsive": 4,
  "__NOT_TAGGED__": 5,
  "On Hold": 6,
};

function comboTagRank(requirement: Requirement, combo: AllocationCombination): number {
  let worst = 0;
  for (const entry of combo.entries ?? []) {
    if (!entry?.lender) continue;
    const status = getLenderTagStatus(requirement, entry.lender) || "__NOT_TAGGED__";
    const r = TAG_STATUS_RANK[status] ?? 5;
    if (r > worst) worst = r;
  }
  return worst;
}

function overrideKey(ov: SimulationOverride): string {
  return ov.policyId ? String(ov.policyId) : String(ov.lenderId);
}

function findMatchedForOverride(
  matched: EligibilityResult["matched"],
  ov: SimulationOverride
) {
  return matched.find((m) =>
    ov.policyId
      ? String(m.policyId) === String(ov.policyId)
      : String(m.lender?.id) === String(ov.lenderId)
  );
}

function buildSimOverrides(matched: EligibilityResult["matched"]): SimulationOverride[] {
  return matched.map((m) => ({
    lenderId: String(m.lender.id),
    policyId: m.policyId ? String(m.policyId) : undefined,
    rejected: false,
    acceptedExposure: m.lender.preferences?.maxExposure || 0,
  }));
}

function FieldCheckRow({ check }: { check: FieldCheck }) {
  const passed = check.passed === true;

  return (
    <div className="flex items-start gap-2 py-1.5 text-xs rounded-md px-2 -mx-2 hover:bg-muted/30 transition-colors">
      {passed ?
      <div className="h-4 w-4 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
          <Check className="h-2.5 w-2.5 text-emerald-600" strokeWidth={3} />
        </div> :

      <div className="h-4 w-4 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
          <X className="h-2.5 w-2.5 text-red-600" strokeWidth={3} />
        </div>
      }
      <div className="min-w-0 flex-1 leading-relaxed">
        <span className="font-medium text-foreground">{check.field}</span>
        <span className="text-muted-foreground/50"> — </span>
        <span className="break-words text-muted-foreground">{check.detail}</span>
      </div>
    </div>
  );
}

export function AIMatchPanel({ open, onOpenChange, requirement }: AIMatchPanelProps) {

  const [activeTab, setActiveTab] = useState("matched");
  const [simOverrides, setSimOverrides] = useState<SimulationOverride[]>([]);
  const [simOpen, setSimOpen] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [eligibility, setEligibility] = useState<EligibilityResult>({ matched: [], notMatched: [] });
  const [combinations, setCombinations] = useState<AllocationCombination[]>([]);
  const [analysis, setAnalysis] = useState("");
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [simResetting, setSimResetting] = useState(false);
  const overridesInitialized = useRef(false);
  const baselineMatchRef = useRef<{ combinations: AllocationCombination[]; analysis: string } | null>(null);

  const rankCombinations = useCallback((raw: AllocationCombination[] = []) => {
    if (!requirement || !raw.length) return raw ?? [];
    try {
      return raw
        .map((c) => filterRejectedFromCombo(requirement, c, requirement.quantum))
        .map((c, i) => ({ c, i, r: comboTagRank(requirement, c) }))
        .sort((a, b) => a.r - b.r || a.i - b.i)
        .map((x) => x.c);
    } catch {
      return raw;
    }
  }, [requirement]);

  const allocationMatched = useMemo(
    () =>
      requirement
        ? eligibility.matched.filter((m) => !isLenderRejected(requirement, m.lender))
        : eligibility.matched,
    [eligibility.matched, requirement]
  );

  const initOverrides = useCallback(() => {
    setSimOverrides(buildSimOverrides(allocationMatched));
    overridesInitialized.current = true;
  }, [allocationMatched]);

  const hasSimChanges = useMemo(() => {
    return simOverrides.some((o) => {
      const orig = findMatchedForOverride(allocationMatched, o);
      if (!orig) return false;
      const origMax = orig.lender.preferences?.maxExposure || 0;
      return o.rejected || o.acceptedExposure !== origMax;
    });
  }, [simOverrides, allocationMatched]);

  const requirementKey = requirement ? String(requirement.id ?? requirement.requirementId ?? "") : "";

  // Initial load when panel opens
  useEffect(() => {
    if (!open || !requirement || !requirementKey) return;

    let cancelled = false;
    const loadMatch = async () => {
      setMatchLoading(true);
      setMatchError(null);
      try {
        const result = await fetchLenderMatch({
          requirementId: requirementKey,
          requirement: requirementKey ? undefined : toMatchRequirement(requirement),
        });
        if (cancelled) return;
        setEligibility(result.eligibility);
        const ranked = rankCombinations(result.combinations);
        baselineMatchRef.current = { combinations: ranked, analysis: result.analysis };
        setCombinations(ranked);
        setAnalysis(result.analysis);
        const matched = result.eligibility.matched.filter(
          (m) => !requirement || !isLenderRejected(requirement, m.lender)
        );
        if (matched.length > 0) {
          setSimOverrides(buildSimOverrides(matched));
          overridesInitialized.current = true;
        }
      } catch (err) {
        console.error("lender match load failed:", err);
        if (!cancelled) {
          setMatchError("Failed to load lender match results. Please try again.");
          setEligibility({ matched: [], notMatched: [] });
          setCombinations([]);
          setAnalysis("");
        }
      } finally {
        if (!cancelled) setMatchLoading(false);
      }
    };

    loadMatch();
    return () => {
      cancelled = true;
    };
    // rankCombinations intentionally omitted — stable fetch keyed by requirement id only
  }, [open, requirementKey]);

  // Re-run allocation when simulator overrides change
  useEffect(() => {
    if (!open || !requirementKey || !hasSimChanges || !overridesInitialized.current) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await fetchLenderMatch({
          requirementId: requirementKey,
          overrides: simOverrides,
        });
        if (cancelled) return;
        setCombinations(rankCombinations(result.combinations));
        setAnalysis(result.analysis);
      } catch (err) {
        console.error("lender match simulate failed:", err);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, requirementKey, hasSimChanges, simOverrides]);

  const updateOverride = (key: string, patch: Partial<SimulationOverride>) => {
    setSimOverrides((prev) =>
      prev.map((o) => (overrideKey(o) === key ? { ...o, ...patch } : o))
    );
  };

  const resetSimulator = useCallback(async () => {
    if (!requirementKey || allocationMatched.length === 0) return;

    setSimResetting(true);
    const defaults = buildSimOverrides(allocationMatched);
    setSimOverrides(defaults);

    try {
      if (baselineMatchRef.current) {
        setCombinations(baselineMatchRef.current.combinations);
        setAnalysis(baselineMatchRef.current.analysis);
      } else {
        const result = await fetchLenderMatch({ requirementId: requirementKey });
        const ranked = rankCombinations(result.combinations);
        baselineMatchRef.current = { combinations: ranked, analysis: result.analysis };
        setCombinations(ranked);
        setAnalysis(result.analysis);
      }
    } catch (err) {
      console.error("lender match reset failed:", err);
    } finally {
      setSimResetting(false);
    }
  }, [requirementKey, allocationMatched, rankCombinations]);

  const handleShowAnalysis = () => {
    setAnalysisLoading(true);
    setShowAnalysis(false);
    setTimeout(() => {
      setAnalysisLoading(false);
      setShowAnalysis(true);
    }, 1800);
  };

  if (!requirement) return null;

  const bestCombo = combinations[0];
  const totalPolicies = eligibility.matched.length + eligibility.notMatched.length;

  return (
    <Sheet open={open} onOpenChange={(v) => {
      if (!v) {
        setSimOverrides([]);
        setSimOpen(false);
        setShowAnalysis(false);
        setAnalysisLoading(false);
        setMatchError(null);
        setActiveTab("matched");
        overridesInitialized.current = false;
        baselineMatchRef.current = null;
      } else if (!overridesInitialized.current && allocationMatched.length > 0) {
        initOverrides();
      }
      onOpenChange(v);
    }}>
      <SheetContent
        side="right"
        overlayClassName="z-[200] bg-black/70"
        className="z-[210] flex h-full max-h-screen w-full flex-col gap-0 overflow-hidden !bg-background p-0 sm:max-w-xl border-l border-[hsl(var(--ai-border))] text-foreground shadow-2xl isolate"
      >
        {/* Header */}
        <SheetHeader className="relative shrink-0 overflow-hidden border-b bg-gradient-to-br from-[hsl(var(--ai-surface))] to-background p-5 pb-4 text-left">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--ai-glow)/0.1),transparent_70%)]" />
          <div className="absolute top-0 left-0 h-[1px] w-full bg-gradient-to-r from-transparent via-[hsl(var(--brand))] to-transparent opacity-40" />
          <div className="relative flex items-center gap-3 pr-8">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] shadow-lg shadow-[hsl(var(--brand))]/25 ring-2 ring-white/20">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle className="flex items-center gap-2 text-base text-foreground">
                AI Lender Intelligence
                <Badge className="h-4 border-0 bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] px-1.5 text-[9px] font-normal text-white">BETA</Badge>
              </SheetTitle>
              <SheetDescription className="mt-0.5 text-xs text-muted-foreground">
                #{requirement.requirementId ?? requirement.id} · ₹{formatAmount(requirement.quantum)} · {requirement.tenure}mo
                {requirement.industry && ` · ${requirement.industry}`}
              </SheetDescription>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-surface))] px-2.5 py-1">
              <CheckCircle2 className="h-3 w-3 text-[hsl(var(--status-success))]" />
              <span className="text-[10px] font-semibold">{eligibility.matched.length}/{totalPolicies}</span>
            </div>
          </div>
        </SheetHeader>

        {matchError && (
          <div className="mx-5 mt-4 flex shrink-0 items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
            <p className="text-xs text-destructive">{matchError}</p>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 px-5 pt-4">
            <TabsList className="grid h-10 w-full grid-cols-3 gap-1 rounded-lg border border-border bg-muted/30 p-1">
              <TabsTrigger value="matched" className={MATCH_TAB_TRIGGER}>
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Matched
                <Badge variant="secondary" className={MATCH_TAB_BADGE}>{eligibility.matched.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="not-matched" className={MATCH_TAB_TRIGGER}>
                <XCircle className="h-3.5 w-3.5 shrink-0" />
                Not Matched
                <Badge variant="secondary" className={MATCH_TAB_BADGE}>{eligibility.notMatched.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="allocation" className={MATCH_TAB_TRIGGER}>
                <BarChart3 className="h-3.5 w-3.5 shrink-0" />
                Allocation
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/* ─── MATCHED TAB ─── */}
          <TabsContent value="matched" className="m-0 px-5 pb-4 pt-0 focus-visible:outline-none data-[state=inactive]:hidden">
            <div className="space-y-3 pt-3">
              {matchLoading ? (
                <MatchLoadingState />
              ) : eligibility.matched.length === 0 ?
              <EmptyState
                icon={<CheckCircle2 className="h-8 w-8 text-muted-foreground/40" />}
                title="No lenders matched"
                description="No lenders meet all eligibility criteria for this requirement." /> :


              <Accordion type="multiple" className="w-full space-y-3">
                  {eligibility.matched.map((result) => {
                  const prefs = result.lender?.preferences;
                  const checks = result.fieldChecks ?? [];
                  const passCount = checks.filter((fc) => fc.passed).length;
                  const totalChecks = checks.length || 1;
                  const itemKey = String(result.policyId ?? `${result.lender?.id}-${result.policyName ?? ""}`);
                  if (!itemKey) return null;
                  return (
                    <AccordionItem key={itemKey} value={itemKey} className="w-full overflow-hidden rounded-xl border border-border bg-card">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-[hsl(var(--ai-surface))]/50 [&[data-state=open]]:border-b [&[data-state=open]]:border-border">
                          <div className="flex w-full min-w-0 items-center gap-2.5 pr-2">
                            <LenderLogo lender={result.lender} />
                            <div className="text-left min-w-0">
                              <p className="text-sm font-semibold truncate">{result.lender?.name ?? "Unknown lender"}</p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <Badge variant="outline" className="text-[10px] h-4 truncate max-w-[160px]">{result.policyName || "—"}</Badge>
                                {prefs && prefs.maxExposure > 0 &&
                              <span className="text-[10px] text-muted-foreground">₹{formatAmount(prefs.minExposure)}–₹{formatAmount(prefs.maxExposure)}</span>
                              }
                              </div>
                            </div>
                            <div className="ml-auto mr-2 flex items-center gap-2 shrink-0">
                              <Progress value={(passCount / totalChecks) * 100} className="w-16 h-1.5" />
                              <span className="text-[10px] font-semibold text-[hsl(var(--status-success))] tabular-nums">{passCount}/{totalChecks}</span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4 pt-0">
                          {/* Field-level checklist — same as Not Matched */}
                          <div className="space-y-0.5 mt-1 mb-3">
                            {checks.map((fc, i) => <FieldCheckRow key={i} check={fc} />)}
                          </div>

                          {prefs && <InvestmentPreferenceBlock prefs={prefs} />}
                        </AccordionContent>
                      </AccordionItem>);

                })}
                </Accordion>
              }
            </div>
          </TabsContent>

          {/* ─── NOT MATCHED TAB ─── */}
          <TabsContent value="not-matched" className="m-0 px-5 pb-4 pt-0 focus-visible:outline-none data-[state=inactive]:hidden">
            <div className="space-y-3 pt-3">
              {matchLoading ? (
                <MatchLoadingState />
              ) : eligibility.notMatched.length === 0 ?
              <EmptyState
                icon={<CheckCircle2 className="h-8 w-8 text-[hsl(var(--status-success))]/60" />}
                title="All lenders matched!"
                description="Every configured lender passes the eligibility criteria." /> :


              <Accordion type="multiple" className="w-full space-y-3">
                  {eligibility.notMatched.map(({ lender, fieldChecks, policyName, policyId }) => {
                  const checks = fieldChecks ?? [];
                  const passCount = checks.filter((fc) => fc.passed).length;
                  const totalChecks = checks.length || 1;
                  const itemKey = String(policyId ?? `${lender?.id}-${policyName ?? ""}`);
                  if (!itemKey) return null;
                  const prefs = lender?.preferences;
                  return (
                    <AccordionItem key={itemKey} value={itemKey} className="w-full overflow-hidden rounded-xl border border-border bg-card">
                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/20 [&[data-state=open]]:border-b [&[data-state=open]]:border-border">
                          <div className="flex w-full min-w-0 items-center gap-2.5 pr-2">
                            <LenderLogo lender={lender} />
                            <div className="text-left min-w-0">
                              <p className="text-sm font-semibold truncate">{lender?.name ?? "Unknown lender"}</p>
                              <Badge variant="outline" className="text-[10px] h-4 mt-0.5 truncate max-w-[160px]">{policyName || "—"}</Badge>
                            </div>
                            <div className="ml-auto mr-2 flex items-center gap-2 shrink-0">
                              <Progress value={(passCount / totalChecks) * 100} className="w-16 h-1.5 [&>div]:bg-destructive" />
                              <span className="text-[10px] font-semibold text-destructive tabular-nums">{passCount}/{totalChecks}</span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-3 pt-0">
                          <div className="space-y-0.5 mt-1">
                            {checks.map((fc, i) => <FieldCheckRow key={i} check={fc} />)}
                          </div>
                          {prefs && <InvestmentPreferenceBlock prefs={prefs} />}
                        </AccordionContent>
                      </AccordionItem>);

                })}
                </Accordion>
              }
            </div>
          </TabsContent>

          {/* ─── ALLOCATION TAB ─── */}
          <TabsContent value="allocation" className="m-0 px-5 pb-4 pt-0 focus-visible:outline-none data-[state=inactive]:hidden">
            <div className="space-y-4 pt-3">
              {matchLoading ? (
                <MatchLoadingState />
              ) : combinations.length === 0 && allocationMatched.length === 0 ?
              <EmptyState
                icon={<BarChart3 className="h-8 w-8 text-muted-foreground/40" />}
                title="No allocation possible"
                description="No lenders meet the eligibility criteria." /> :


              <>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl border bg-gradient-to-br from-card to-[hsl(var(--ai-surface))]/50 p-3 text-center relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] opacity-40" />
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Required</p>
                      <p className="text-base font-bold mt-1">₹{formatAmount(requirement.quantum)}</p>
                    </div>
                    <div className="rounded-xl border bg-gradient-to-br from-card to-[hsl(var(--status-success))]/5 p-3 text-center relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-[2px] bg-[hsl(var(--status-success))] opacity-40" />
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center justify-center gap-1.5 flex-wrap">
                        <span>Allocated</span>
                        {hasSimChanges &&
                        <Badge className="text-[8px] h-4 px-1.5 normal-case tracking-normal bg-[hsl(var(--brand-subtle))] text-[hsl(var(--brand))] border-[hsl(var(--brand))]/30">
                          Simulated
                        </Badge>
                        }
                      </p>
                      <p className="text-base font-bold mt-1 text-[hsl(var(--status-success))]">
                        ₹{formatAmount(bestCombo?.totalAllocated || 0)}
                      </p>
                    </div>
                    <div className="rounded-xl border bg-gradient-to-br from-card to-destructive/5 p-3 text-center relative overflow-hidden">
                      <div className={`absolute top-0 left-0 w-full h-[2px] ${(bestCombo?.shortfall || 0) > 0 ? "bg-destructive" : "bg-[hsl(var(--status-success))]"} opacity-40`} />
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Shortfall</p>
                      <p className={`text-base font-bold mt-1 ${(bestCombo?.shortfall || 0) > 0 ? "text-destructive" : "text-[hsl(var(--status-success))]"}`}>
                        ₹{formatAmount(bestCombo?.shortfall || 0)}
                      </p>
                    </div>
                  </div>

                  {/* Structuring Simulator */}
                  <TooltipProvider>
                    {allocationMatched.length > 0 &&
                  <Collapsible open={simOpen} onOpenChange={setSimOpen}>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" size="sm" className="w-full justify-between text-xs h-9 border-[hsl(var(--ai-border))] bg-[hsl(var(--ai-surface))]/30 hover:bg-[hsl(var(--ai-surface))]">
                            <span className="flex items-center gap-1.5">
                              <SlidersHorizontal className="h-3.5 w-3.5 text-[hsl(var(--ai-accent))]" />
                              <span className="font-medium">Structuring Simulator</span>
                              {hasSimChanges && <Badge className="text-[8px] h-4 bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] text-white border-0 ml-1">Active</Badge>}
                            </span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform text-muted-foreground ${simOpen ? "rotate-180" : ""}`} />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="rounded-xl border border-[hsl(var(--ai-border))] bg-gradient-to-br from-[hsl(var(--ai-surface))]/50 to-background p-3 mt-2 space-y-2">
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                              <Zap className="h-3 w-3 text-[hsl(var(--status-warning))]" />
                              Adjust lender exposure or reject to see updated structures instantly.
                            </p>
                            {simOverrides.map((ov) => {
                          const matchedLender = findMatchedForOverride(allocationMatched, ov);
                          const lender = matchedLender?.lender;
                          if (!lender) return null;
                          const ovKey = overrideKey(ov);
                          const origMax = lender.preferences?.maxExposure || 0;
                          const origMin = lender.preferences?.minExposure || 0;
                          const isModified = ov.rejected || ov.acceptedExposure !== origMax;
                          const exceedsMax = !ov.rejected && ov.acceptedExposure > origMax && origMax > 0;
                          const belowMin = !ov.rejected && (ov.acceptedExposure || 0) > 0 && (ov.acceptedExposure || 0) < origMin && origMin > 0;
                          const hasWarning = exceedsMax || belowMin;
                          return (
                            <div key={ovKey} className={`flex items-center gap-3 p-2.5 rounded-lg border bg-card transition-all ${ov.rejected ? "opacity-40 bg-muted/50" : ""} ${isModified && !ov.rejected ? "border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand))]/5" : ""}`}>
                                  <LenderLogo lender={lender} />
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-medium truncate ${ov.rejected ? "line-through text-muted-foreground" : ""}`}>
                                      {lender.name ?? "Unknown lender"}
                                    </p>
                                    {matchedLender?.policyName &&
                                    <p className="text-[10px] text-muted-foreground truncate">{matchedLender.policyName}</p>
                                    }
                                    <p className="text-[10px] text-muted-foreground">
                                      ₹{formatAmount(origMin)} – ₹{formatAmount(origMax)}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="flex flex-col items-end gap-1">
                                      <Label className="text-[9px] text-muted-foreground">Accepted Exposure</Label>
                                      <div className="rounded-md border border-[hsl(var(--brand))]/20 bg-[hsl(var(--brand-subtle))] p-0.5">
                                        <div className="relative h-7 w-32">
                                          <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-[11px] font-semibold text-[hsl(var(--brand))]">
                                            ₹
                                          </span>
                                          <Input
                                            type="number"
                                            disabled={ov.rejected}
                                            value={ov.acceptedExposure || 0}
                                            onChange={(e) => updateOverride(ovKey, { acceptedExposure: Number(e.target.value) })}
                                            className={`h-7 w-full rounded-[4px] border-0 bg-white/90 text-xs text-right pl-6 shadow-none focus-visible:ring-1 focus-visible:ring-[hsl(var(--brand))]/40 focus-visible:ring-offset-0 ${hasWarning ? "pr-8" : "pr-2"}`}
                                          />
                                          {exceedsMax &&
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button
                                                type="button"
                                                className="absolute inset-y-0 right-1.5 flex items-center cursor-help"
                                                aria-label="Exposure exceeds policy maximum"
                                              >
                                                <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--status-warning))]" />
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="text-xs max-w-[200px]">
                                              Exceeds lender's max exposure policy of ₹{formatAmount(origMax)}
                                            </TooltipContent>
                                          </Tooltip>
                                          }
                                          {belowMin && !exceedsMax &&
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button
                                                type="button"
                                                className="absolute inset-y-0 right-1.5 flex items-center cursor-help"
                                                aria-label="Exposure below policy minimum"
                                              >
                                                <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--status-warning))]" />
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="text-xs max-w-[200px]">
                                              Below lender's min exposure policy of ₹{formatAmount(origMin)}
                                            </TooltipContent>
                                          </Tooltip>
                                          }
                                        </div>
                                      </div>
                                      <p className="text-[9px] text-muted-foreground text-right w-32">
                                        {formatAmountCompact(ov.acceptedExposure || 0)}
                                      </p>
                                    </div>
                                    <div className="flex flex-col items-center gap-1">
                                      <Label className="text-[9px] text-muted-foreground">Reject</Label>
                                      <Switch
                                    checked={ov.rejected || false}
                                    onCheckedChange={(v) => updateOverride(ovKey, { rejected: v })}
                                    className="scale-75" />
                                  
                                    </div>
                                  </div>
                                </div>);

                        })}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-7 w-full text-muted-foreground hover:text-foreground"
                              disabled={simResetting || (!hasSimChanges && !simOverrides.some((o) => o.rejected))}
                              onClick={resetSimulator}
                            >
                              <RefreshCw className={`h-3 w-3 mr-1 ${simResetting ? "animate-spin" : ""}`} />
                              {simResetting ? "Resetting..." : "Reset to defaults"}
                            </Button>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                  }
                  </TooltipProvider>

                  {/* Shortfall message */}
                  {hasSimChanges && bestCombo && bestCombo.shortfall > 0 &&
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                      <p className="text-xs text-destructive">Additional lender capacity required to fulfil this deal.</p>
                    </div>
                }

                  {/* Structure Option Cards */}
                  {combinations.length === 0 ?
                <div className="rounded-xl border border-dashed border-[hsl(var(--status-warning))]/40 bg-[hsl(var(--status-warning))]/5 p-6 text-center">
                      <AlertTriangle className="h-6 w-6 mx-auto text-[hsl(var(--status-warning))] mb-2" />
                      <p className="text-sm font-medium">No structures available</p>
                      <p className="text-xs text-muted-foreground mt-1">All lenders may be rejected. Reset simulator to regenerate.</p>
                    </div> :

                <div className="space-y-3">
                      {combinations.map((combo, idx) =>
                  <StructureOptionCard
                    key={combo?.id ?? `combo-${idx}`}
                    combo={combo}
                    quantum={requirement.quantum}
                    requirement={requirement}
                    hasSimChanges={hasSimChanges}
                    isFirst={idx === 0}
                  />
                  )}
                    </div>
                }
                </>
              }
            </div>
          </TabsContent>
          </div>

        </Tabs>
      </SheetContent>
    </Sheet>);

}

// ─── Structure Option Card ──────────────────────────────────────────

function StructureOptionCard({
  combo, quantum, requirement, hasSimChanges, isFirst
}: {combo: AllocationCombination;quantum: number;requirement: Requirement;hasSimChanges: boolean;isFirst: boolean;}) {
  const entries = combo?.entries ?? [];
  const totalAllocated = combo?.totalAllocated ?? 0;
  const shortfall = combo?.shortfall ?? 0;
  const lendersUsed = combo?.lendersUsed ?? entries.length;
  const fulfilled = combo?.fulfilled ?? false;

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${isFirst ? "border-[hsl(var(--brand))]/30 shadow-md shadow-[hsl(var(--brand))]/5" : ""} bg-card`}>
      {/* Card Header */}
      <div className={`px-4 py-2.5 flex items-center justify-between ${isFirst ? "bg-gradient-to-r from-[hsl(var(--brand))]/5 to-[hsl(var(--brand-2))]/5" : "bg-muted/20"}`}>
        <div className="flex items-center gap-2">
          {isFirst ?
          <div className="h-5 w-5 rounded-md bg-gradient-to-br from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] flex items-center justify-center">
              <Sparkles className="h-3 w-3 text-white" />
            </div> :
          <div className="h-5 w-5 rounded-md bg-muted flex items-center justify-center">
              <Layers className="h-3 w-3 text-muted-foreground" />
            </div>
          }
          <span className="text-xs font-semibold">{combo?.label ?? "Structure Option"}</span>
          {isFirst && <Badge className="text-[8px] h-4 bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))] text-white border-0">Recommended</Badge>}
          {hasSimChanges &&
          <Badge className="text-[8px] h-4 bg-[hsl(var(--status-warning))]/10 text-[hsl(var(--status-warning))] border-[hsl(var(--status-warning))]/30 border">Simulated</Badge>
          }
        </div>
      </div>

      {/* Lender allocation rows */}
      <div className="px-4 py-3 space-y-3">
        {entries.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">No lender allocations in this option.</p>
        ) : entries.map((entry, entryIdx) => {
          const lender = entry?.lender;
          if (!lender || isLenderRejected(requirement, lender)) return null;

          const lenderId = String(lender.id ?? entryIdx);
          const lenderName = lender.name || "Unknown lender";
          const allocation = Number(entry?.allocation ?? 0);
          const pct = quantum > 0 ? Math.round(allocation / quantum * 100) : 0;
          const tagStatus = getLenderTagStatus(requirement, lender);
          const tagCfg = tagStatus ? getStatusConfig(tagStatus as LenderStatus) : null;
          const TagIcon = tagCfg?.icon;

          return (
            <div key={lenderId} className="flex items-center gap-3">
              <LenderLogo lender={lender} />

              <span className="text-sm font-medium shrink-0 min-w-[100px] truncate">
                {lenderName}
              </span>

              <div className="flex-1 min-w-0">
                <div className="h-7 rounded-lg bg-muted/60 overflow-hidden relative">
                  <div
                    className={`h-full rounded-lg flex items-center justify-center transition-all duration-500 ${
                    isFirst
                      ? "bg-gradient-to-r from-[hsl(var(--brand))] to-[hsl(var(--brand-2))]"
                      : "bg-gradient-to-r from-primary-dark/70 to-primary-dark/50"
                    }`}
                    style={{ width: `${Math.min(100, Math.max(pct + 10, 20))}%` }}
                  >
                    <span className="text-[11px] font-bold text-white">
                      {pct}%
                    </span>
                  </div>
                </div>
              </div>

              <span className="text-sm font-semibold shrink-0 tabular-nums">
                ₹{formatAmount(allocation)}
              </span>

              {tagCfg ? (
                <Badge variant={tagCfg.variant as "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "info" | "purple"} className="text-[9px] h-5 px-1.5 gap-1 shrink-0">
                  {TagIcon ? <TagIcon className="h-2.5 w-2.5" /> : null}
                  {tagStatus}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[9px] h-5 px-1.5 shrink-0 text-muted-foreground">
                  Not Tagged
                </Badge>
              )}
            </div>);
        })}
      </div>

      {/* Footer summary */}
      <div className={`px-4 py-2.5 border-t flex items-center justify-between ${isFirst ? "bg-[hsl(var(--ai-surface))]/30" : "bg-muted/10"}`}>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span><span className="font-semibold text-foreground">{lendersUsed}</span> lender{lendersUsed > 1 ? "s" : ""}</span>
          <span>₹{formatAmount(totalAllocated)} allocated</span>
        </div>
        {fulfilled ?
        <Badge variant="outline" className="text-[10px] h-5 bg-[hsl(var(--status-success))]/10 text-[hsl(var(--status-success))] border-[hsl(var(--status-success))]/30 gap-1">
            <CheckCircle2 className="h-3 w-3" /> Fully covered
          </Badge> :
        <Badge variant="outline" className="text-[10px] h-5 bg-destructive/10 text-destructive border-destructive/30 gap-1">
            <AlertTriangle className="h-3 w-3" /> Shortfall ₹{formatAmount(shortfall)}
          </Badge>
        }
      </div>
    </div>);
}
function MatchLoadingState() {
  return (
    <div className="rounded-xl border border-dashed border-[hsl(var(--ai-border))] p-8 text-center bg-[hsl(var(--ai-surface))]/30">
      <Loader2 className="h-8 w-8 mx-auto text-[hsl(var(--brand))] animate-spin mb-2" />
      <p className="text-sm font-medium">Running lender match...</p>
      <p className="text-xs text-muted-foreground mt-1">Evaluating policies and allocation options</p>
    </div>
  );
}

function EmptyState({ icon, title, description }: {icon: React.ReactNode;title: string;description: string;}) {
  return (
    <div className="rounded-xl border border-dashed border-[hsl(var(--ai-border))] p-8 text-center bg-[hsl(var(--ai-surface))]/30">
      <div className="mx-auto mb-2">{icon}</div>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>);

}
// ─── Investment Preference Block ─────────────────────────────────────
function collectPreferredAssetsFromPolicy(prefs: InvestmentPreference): string[] {
  const fromPolicy = (prefs.policyPreferredAssets || []).map((a) => String(a).trim()).filter(Boolean);
  const fromRules: string[] = [];
  for (const rule of prefs.assetCategoryRules || []) {
    for (const asset of rule.preferredAssets || []) {
      const value = String(asset ?? "").trim();
      if (value) fromRules.push(value);
    }
  }
  return Array.from(new Set([...fromPolicy, ...fromRules]));
}

function InvestmentPreferenceBlock({ prefs }: { prefs: InvestmentPreference }) {
  const preferredAssets = collectPreferredAssetsFromPolicy(prefs);
  const frequency = (prefs.frequency || []).filter(Boolean);
  const caseTypes = (prefs.caseTypes || []).filter(Boolean);
  const dfMin = prefs.discountingFactorMin || 0;
  const dfMax = prefs.discountingFactorMax || 0;
  const hasDF = dfMin > 0 || dfMax > 0;
  const tenureRanges = (prefs.assetCategoryRules || [])
    .filter(r => r.tenureMin > 0 || r.tenureMax > 0)
    .map(r => `${r.category}: ${r.tenureMin}–${r.tenureMax}mo`);
  const note = (prefs.note || "").trim();

  const rows: { label: string; value: React.ReactNode }[] = [];
  rows.push({
    label: "Preferred Assets",
    value: preferredAssets.length > 0 ? preferredAssets.join(", ") : "-",
  });
  if (frequency.length > 0) rows.push({ label: "Rental Frequency", value: frequency.join(", ") });
  if (hasDF) rows.push({ label: "Discounting Factor", value: `${dfMin}–${dfMax}%` });
  if (caseTypes.length > 0) rows.push({ label: "Case Type", value: caseTypes.join(", ") });
  if (tenureRanges.length > 0) rows.push({ label: "Tenure", value: tenureRanges.join(" · ") });
  if (note) rows.push({ label: "Note", value: <span className="whitespace-pre-line">{note}</span> });

  if (rows.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-dashed">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
        <Sparkles className="h-2.5 w-2.5 text-[hsl(var(--brand))]" />
        Investment Preference
      </p>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="text-xs flex gap-2">
            <span className="text-muted-foreground font-medium shrink-0 min-w-[120px]">{r.label}</span>
            <span className="text-foreground/90">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
